// 전투 코어 연출(소유: VFX, COMBAT-FEEL 2.2~2.5): 공격 자석 링 · 무방비(자세 붕괴) 표식 · 처형 · 공중 연타 · 폭주(연속 처치).
// 게임은 값·이벤트만 낸다(view.magnet, view.mobs.guardBroken, Ev.GuardBreak·Juggle·Execute·Rampage). 여기서는 읽기만 하고 그린다.
//
// 드로우콜 2: 빌보드 가산(글리프·교차 참격·세로 속도선) / 바닥 알파(균열·자석 링·무방비 고리·처형·폭주 고리). 모두 InstancedMesh 하나에 인스턴스 속성으로 종류를 넘긴다.
// - 지속 표식(자석 링, 무방비 고리·글리프)은 매 프레임 begin → put → flush로 다시 쓴다(몹을 따라간다).
// - 일회성(균열, 처형 고리·교차 참격, 연타 속도선, 폭주 고리)은 TimedSlots 풀(고정 용량)에 넣어 수명이 끝나면 지운다.
// 카메라 펀치·히트스톱은 전투 레인이 이벤트(Ev.CameraPunch)·시뮬레이션으로 이미 낸다 — 여기서 카메라를 건드리지 않는다.
// 전체 화면 섬광은 처형 보상 한 번뿐이고 공용 FlashLimiter(1초 3회)를 거친다. 프레임 루프에서 새 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { abs, atan, clamp, exp, float, fract, fwidth, instancedDynamicBufferAttribute, length, max, min, mix, pow, sin, smoothstep, step, uv, vec2, vec3 } from 'three/tsl';
import type { Node } from 'three/webgpu';
import { MOBS, MOB_CAPACITY, MOB_STATS, POSTURE, RAMPAGE } from '../shared/config';
import type { GameEvent } from '../shared/events';
import { MobState, Scene, combatScene, type GameView } from '../shared/types';
import type { FlashLimiter } from './flash';
import { FadeTracker, TimedSlots, requestExecuteFlash, vulnerableView, type VulnerableView } from './combatCoreMath';
import type { ParticleFx } from './particles';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any

const TAU = Math.PI * 2;
/** 바닥 표식 높이(m): 경고 데칼(0.03, renderOrder 5)보다 아래·먼저 그려 경고선을 덮지 않는다 */
const GROUND_Y = 0.022;
/** 지속 바닥 표식 크기 환산: 고리 반경이 쿼드 반폭의 0.9에 있다 */
const RING_AT = 0.9;
/** 종류별 몹 키(m) — render/mobs.ts TARGET_H와 같은 값(머리 위 글리프 높이용 복사. 모델 교체 때 함께 본다) */
const HEAD_H = [1.45, 1.6, 1.85, 1.5] as const;
const GOLD = [2.4, 1.6, 0.45] as const;
const CRIMSON = [2.4, 0.3, 0.42] as const;
const WHITE_GOLD = [2.3, 1.95, 1.2] as const;

/** 종류 번호(셰이더와 같다) */
export const BK = { glyph: 0, cross: 1, vlines: 2, airRing: 3 } as const;
export const PK = { crack: 0, magnet: 1, vuln: 2, exec: 3, rampage: 4 } as const;

/** board: 빌보드 가산(글리프·교차 참격·속도선) / paint: 바닥 알파(균열·자석 링·무방비 고리·처형·폭주 고리 — 밝은 돌바닥에서도 읽히게 어두운 테두리를 두른다) */
type Family = 'board' | 'paint';

const hash = (x: N): N => fract(sin(x.mul(127.1).add(311.7)).mul(43758.5453));
/** 두께 w 안이면 1, 바깥으로 aa에 걸쳐 0 */
const band = (d: N, w: N, aa: N): N => float(1).sub(smoothstep(max(w.sub(aa), 0), w.add(aa), abs(d)));

function buildMaterial(family: Family, attr: THREE.InstancedBufferAttribute): THREE.MeshBasicNodeMaterial {
  const M = instancedDynamicBufferAttribute(attr, 'vec4') as unknown as Node<'vec4'>;
  const kind: N = M.x, alpha: N = M.y, t: N = M.z, aux: N = M.w;
  const is = (k: number): N => float(1).sub(abs(kind.sub(k)).min(1));
  // 진행률(0..1). 지속 표식은 t가 초 단위라 쓰지 않는다
  const u: N = clamp(t, 0, 1);
  const p: N = uv().sub(0.5).mul(2);
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  mat.fog = false;
  mat.forceSinglePass = true;

  if (family === 'board') {
    mat.blending = THREE.AdditiveBlending;
    const aa: N = max(fwidth(abs(p.x).add(abs(p.y))), 0.004).mul(1.5);
    // 0 글리프: 박동하는 금색 마름모 테두리 + 속이 찬 흰금색 심(머리 위, 처형 시점을 알린다)
    const pulse: N = sin(t.mul(TAU).mul(aux)).mul(0.5).add(0.5);
    const dia: N = abs(p.x).add(abs(p.y));
    const outline: N = band(dia.sub(pulse.mul(0.04).add(0.58)), float(0.07), aa);
    const core: N = float(1).sub(smoothstep(0.14, 0.22, dia));
    const m0: N = clamp(outline.add(core.mul(0.95)).add(exp(dia.mul(-5)).mul(0.14).mul(pulse)), 0, 1);
    const c0: N = mix(vec3(1.35, 0.8, 0.08), vec3(1.7, 1.45, 0.85), core).mul(m0);
    // 1 교차 참격: 대각 두 줄이 차례로 그어진다. 흰금색 심 + 금색 몸 + 진홍 번짐
    const cs = 0.70710678;
    const blade = (px: N, py: N, head: N): N => {
      const taper: N = max(float(1).sub(pow(max(abs(px), 0.0001), 2.2)), 0);
      const hw: N = taper.mul(0.07).add(0.003);
      const ap: N = abs(py);
      const body: N = float(1).sub(smoothstep(hw.mul(0.8), hw, ap));
      const core2: N = float(1).sub(smoothstep(hw.mul(0.1), hw.mul(0.32), ap));
      const halo: N = exp(ap.div(hw.mul(3.2).add(0.012)).mul(-1.1)).mul(taper).mul(0.55);
      const prog: N = px.mul(0.5).add(0.5);
      const vis: N = float(1).sub(smoothstep(head.sub(0.07), head, prog));
      return vec3(1.8, 0.05, 0.12).mul(halo.mul(1.6)).add(vec3(1.2, 0.66, 0.07).mul(body)).add(vec3(1.6, 1.3, 0.75).mul(core2)).mul(vis);
    };
    const ax: N = p.x.mul(cs).add(p.y.mul(cs)), ay: N = p.y.mul(cs).sub(p.x.mul(cs));
    const bx: N = p.x.mul(cs).sub(p.y.mul(cs)).negate(), by: N = p.y.mul(cs).add(p.x.mul(cs));
    const headA: N = clamp(u.div(0.2), 0, 1).mul(1.1);
    const headB: N = clamp(u.sub(0.1).div(0.2), 0, 1).mul(1.1);
    const fade1: N = float(1).sub(smoothstep(0.45, 1.0, u));
    const c1: N = blade(ax, ay, headA).add(blade(bx, by, headB)).mul(fade1);
    // 2 세로 속도선: 위로 솟는 가는 줄 7개(공중 연타·폭주 오라)
    const cells = 9;
    const gx: N = p.x.mul(0.5).add(0.5).mul(cells);
    const cell: N = gx.floor();
    const fx: N = fract(gx).sub(0.5);
    const h: N = hash(cell), h2: N = hash(cell.add(9));
    const off: N = h.sub(0.5).mul(0.4);
    const head2: N = easeOut(u.mul(h.mul(0.5).add(0.7))).mul(2.2).sub(1.0);
    const tail2: N = head2.sub(h.mul(0.4).add(0.6));
    const g2: N = smoothstep(tail2, head2, p.y);
    const streak: N = pow(g2, 1.4).mul(float(1).sub(smoothstep(head2.sub(0.14), head2, p.y)).mul(0.6).add(0.4));
    const wid: N = mix(float(0.03), float(0.085), g2);
    const line: N = float(1).sub(smoothstep(wid.mul(0.55), wid, abs(fx.sub(off)))).mul(smoothstep(0.1, 0.2, h2)).mul(step(p.y, head2));
    const c2: N = vec3(1.9, 1.35, 0.4).mul(line).mul(streak).mul(float(1).sub(u));
    // 3 공중 파문: 몹 허리 높이에서 퍼지는 납작한 금색 타원 고리(공중 타격 표지 — 땅 충격 고리와 달리 위에 뜬다)
    const er: N = length(vec2(p.x, p.y.mul(2.6)));
    const e5: N = float(1).sub(pow(float(1).sub(u), 3));
    const rr5: N = e5.mul(0.85).add(0.1);
    const ring5: N = band(er.sub(rr5), float(0.05).mul(float(1).sub(u)).add(0.012), aa);
    const c3: N = mix(vec3(1.7, 1.3, 0.5), vec3(1.6, 0.5, 0.15), smoothstep(0.2, 0.9, u)).mul(ring5).mul(pow(max(float(1).sub(u), 0.0001), 1.2));
    mat.colorNode = c0.mul(is(0)).add(c1.mul(is(1))).add(c2.mul(is(2))).add(c3.mul(is(3)));
    mat.opacityNode = alpha;
  } else {
    // 바닥 알파 — 0 균열: 몸 아래에서 갈라져 나가는 방사 균열(어두운 틈 + 금빛 열기)
    const r: N = length(p);
    const ang: N = atan(p.y, p.x);
    const cracks = 9;
    const nn: N = ang.div(TAU).mul(cracks).add(sin(r.mul(31).add(aux)).mul(0.05)).add(sin(r.mul(11).add(aux.mul(2))).mul(0.07));
    const id: N = nn.floor();
    const f: N = abs(fract(nn).sub(0.5));
    const hh: N = hash(id.add(aux));
    const len: N = hh.mul(0.6).add(0.38);
    const grow: N = easeOut(u.div(0.2));
    const reach: N = len.mul(grow);
    const present: N = smoothstep(0.15, 0.25, hash(id.add(9)));
    const dist: N = f.mul(TAU / cracks).mul(r);
    const hw: N = mix(float(0.05), float(0.007), clamp(r.div(max(len, 0.01)), 0, 1));
    const mask: N = float(1).sub(smoothstep(hw.mul(0.7), hw, dist)).mul(float(1).sub(smoothstep(reach.sub(0.05), reach, r))).mul(present).mul(smoothstep(0.03, 0.07, r));
    const hot: N = float(1).sub(smoothstep(hw.mul(0.12), hw.mul(0.5), dist)).mul(float(1).sub(smoothstep(0.1, 0.7, u)));
    const colCrack: N = mix(vec3(0.07, 0.04, 0.025), vec3(1.5, 0.9, 0.12), hot);
    const opCrack: N = mask.mul(float(1).sub(smoothstep(0.55, 1.0, u))).mul(0.92);
    // 1 자석 링: 가는 금색 원(바깥에 어두운 테두리) + 느리게 도는 4개 눈금. 밝은 돌바닥에서 크림색으로 바래지 않게 알파 혼합
    const aa: N = max(fwidth(r), 0.004).mul(1.5);
    const mr: N = r.sub(0.9);
    const seg: N = fract(ang.div(TAU).mul(4).add(t.mul(0.08)));
    const ds: N = min(seg, float(1).sub(seg));
    const tickA: N = float(1).sub(smoothstep(0.012, 0.03, ds)).mul(smoothstep(0.66, 0.72, r)).mul(float(1).sub(smoothstep(0.9, 0.94, r)));
    const core1: N = clamp(band(mr, float(0.032), aa).add(tickA), 0, 1);
    const edge1: N = clamp(band(mr, float(0.058), aa).add(tickA), 0, 1);
    const colMag: N = mix(vec3(0.1, 0.06, 0.02), vec3(1.1, 0.7, 0.08), core1);
    const opMag: N = edge1.mul(0.92);
    // 2 무방비 고리: 박동하는 점선 금색 고리(굵게) + 안쪽 가는 고리 + 옅은 바닥 채움(aux = 맥동 Hz)
    const pulse: N = sin(t.mul(TAU).mul(aux)).mul(0.5).add(0.5);
    const rr: N = pulse.mul(0.04).add(0.86);
    const dseg: N = fract(ang.div(TAU).mul(10).add(t.mul(0.1)));
    const dash: N = smoothstep(0.02, 0.08, dseg).mul(float(1).sub(smoothstep(0.52, 0.58, dseg)));
    const ringC: N = band(r.sub(rr), float(0.04), aa).mul(dash);
    const ringE: N = band(r.sub(rr), float(0.07), aa).mul(smoothstep(0.0, 0.05, dseg).mul(float(1).sub(smoothstep(0.55, 0.6, dseg))).max(dash));
    const inner: N = band(r.sub(0.64), float(0.018), aa).mul(0.8);
    const disc: N = float(1).sub(smoothstep(0.0, 0.9, r)).mul(pulse.mul(0.1).add(0.08));
    const core2: N = clamp(ringC.add(inner), 0, 1);
    const colVul: N = mix(vec3(0.16, 0.07, 0.01), vec3(1.25, 0.66, 0.05), clamp(core2.add(disc.mul(0.4)), 0, 1));
    const opVul: N = clamp(ringE.add(inner.mul(1.2)).add(disc), 0, 1).mul(0.95);
    // 3 처형 충격 고리: 금색 → 진홍으로 번지며 퍼지는 고리(알파 혼합 + 어두운 테두리)
    const dark: N = vec3(0.14, 0.05, 0.03);
    const e3: N = float(1).sub(pow(float(1).sub(u), 3));
    const rr3: N = e3.mul(0.88).add(0.1);
    const w3: N = float(1).sub(u).mul(0.05).add(0.018);
    const core3: N = band(r.sub(rr3), w3, aa), edge3: N = band(r.sub(rr3), w3.mul(1.7).add(0.012), aa);
    const colEx: N = mix(dark, mix(vec3(1.3, 0.9, 0.22), vec3(1.7, 0.1, 0.16), smoothstep(0.05, 0.6, u)), core3);
    const opEx: N = edge3.mul(pow(max(float(1).sub(u), 0.0001), 1.2)).mul(0.95);
    // 4 폭주 고리: 금색 → 따뜻한 주황, 안쪽을 옅은 금빛 오라로 채운다
    const e4: N = float(1).sub(pow(float(1).sub(u), 2.5));
    const rr4: N = e4.mul(0.9).add(0.06);
    const w4: N = float(1).sub(u).mul(0.045).add(0.014);
    const core4: N = band(r.sub(rr4), w4, aa), edge4: N = band(r.sub(rr4), w4.mul(1.7).add(0.01), aa);
    const aura: N = float(1).sub(smoothstep(0.0, rr4, r)).mul(0.16).mul(pow(max(float(1).sub(u), 0.0001), 2));
    const colRa: N = mix(dark, mix(vec3(1.3, 0.85, 0.15), vec3(1.6, 0.32, 0.1), u.mul(0.8)), clamp(core4.add(aura.mul(2)), 0, 1));
    const opRa: N = clamp(edge4.mul(pow(max(float(1).sub(u), 0.0001), 1.1)).add(aura), 0, 1).mul(0.95);
    mat.colorNode = colCrack.mul(is(0)).add(colMag.mul(is(1))).add(colVul.mul(is(2))).add(colEx.mul(is(3))).add(colRa.mul(is(4)));
    mat.opacityNode = opCrack.mul(is(0)).add(opMag.mul(is(1))).add(opVul.mul(is(2))).add(opEx.mul(is(3))).add(opRa.mul(is(4))).mul(alpha);
  }
  return mat;
}

/** 0..1 → 감속 곡선(셰이더용) */
function easeOut(u: N): N { return float(1).sub(pow(max(float(1).sub(clamp(u, 0, 1)), 0.0001), 3)); }

/**
 * 같은 셰이더를 쓰는 인스턴스 묶음. 앞쪽 persistentCap장은 지속 표식(put), 그 뒤는 일회성(spawn)이다.
 * begin() → put()* → flush(dt, camera) 순서로 매 프레임 부른다.
 */
export class MarkPool {
  readonly mesh: THREE.InstancedMesh;
  readonly slots: TimedSlots;
  private readonly mark: THREE.InstancedBufferAttribute;
  private readonly dummy = new THREE.Object3D();
  private readonly total: number;
  /** 이번 프레임에 쓴 지속 표식 수(QA) */
  persistent = 0;
  /** 이번 프레임에 쓴 인스턴스 총수(QA) */
  drawn = 0;

  constructor(readonly family: Family, readonly persistentCap: number, transientCap: number, order: number) {
    this.total = persistentCap + transientCap;
    this.slots = new TimedSlots(transientCap);
    this.mark = new THREE.InstancedBufferAttribute(new Float32Array(this.total * 4), 4);
    this.mark.setUsage(THREE.DynamicDrawUsage);
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), buildMaterial(family, this.mark), this.total);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = order;
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.name = 'combat-core-' + family;
    this.dummy.rotation.order = 'YXZ';
  }

  begin(): void { this.persistent = 0; }

  /** 지속 표식 한 장. kind·alpha·t(초, 맥동 위상)·aux(맥동 Hz 등). 용량을 넘으면 버린다 */
  put(kind: number, x: number, y: number, z: number, size: number, alpha: number, t: number, aux: number, yaw: number, camera: THREE.Camera): void {
    if (this.persistent >= this.persistentCap) return;
    this.write(this.persistent++, kind, x, y, z, size, alpha, t, aux, yaw, camera);
  }

  /** 일회성 표식. delaySec 뒤에 나타난다. size는 월드 m(정사각 한 변) */
  spawn(kind: number, x: number, y: number, z: number, size: number, life: number, yaw = 0, aux = 0, delaySec = 0): void {
    this.slots.spawn(kind, x, y, z, size, life, yaw, aux, delaySec);
  }

  private write(i: number, kind: number, x: number, y: number, z: number, size: number, alpha: number, t: number, aux: number, yaw: number, camera: THREE.Camera): void {
    const d = this.dummy;
    d.position.set(x, y, z);
    if (this.family === 'board') { d.quaternion.copy(camera.quaternion); d.rotateZ(yaw); }
    else d.rotation.set(-Math.PI / 2, 0, yaw);
    d.scale.set(size, size, 1);
    d.updateMatrix();
    this.mesh.setMatrixAt(i, d.matrix);
    const a = this.mark.array as Float32Array;
    a[i * 4] = kind; a[i * 4 + 1] = alpha; a[i * 4 + 2] = t; a[i * 4 + 3] = aux;
  }

  /** dtSec: 일회성 표식을 이만큼 진행하고 지속 표식 뒤에 이어 쓴다 */
  flush(dtSec: number, camera: THREE.Camera): void {
    const s = this.slots;
    s.step(dtSec);
    let n = this.persistent;
    for (let k = 0; k < s.n && n < this.total; k++) {
      if (!s.visible(k)) continue;
      this.write(n++, s.kind[k], s.x[k], s.y[k], s.z[k], s.size[k], 1, s.progress(k), s.aux[k], s.yaw[k], camera);
    }
    this.drawn = n;
    this.mesh.count = n;
    this.mesh.visible = n > 0;
    if (n > 0) { this.mesh.instanceMatrix.needsUpdate = true; this.mark.needsUpdate = true; }
  }

  /** 미리 컴파일용: 인스턴스 하나를 둔다 */
  prewarm(on: boolean): void {
    if (on) { this.mesh.count = Math.max(1, this.drawn); this.mesh.visible = true; }
    else { this.mesh.count = this.drawn; this.mesh.visible = this.drawn > 0; }
  }

  clear(): void { this.slots.clear(); this.persistent = 0; this.drawn = 0; this.mesh.count = 0; this.mesh.visible = false; }

  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

/** QA 기록 콜백(CombatFx.note와 같은 모양) */
export type CoreNote = (k: string, t: number, x: number, z: number, a: number) => void;

export interface CoreState {
  magnetAlpha: number; magnetKey: number; vulnerable: number;
  board: number; paint: number; transients: number;
}

/**
 * 일회성 표식 용량(같은 드로우콜 안에서 인스턴스만 늘어난다). 광역 한 번에 몹 최대 수가 모두 깨지고 처형되는 최악을 담는다:
 * 처형마다 고리 두 장 + 깨짐 균열 한 장 + 그 처치들이 낸 폭주 단계(RAMPAGE.every마다)마다 두 장(Codex 5차: 폭주 표식이 넘쳐 처형 고리를 덮어씀)
 */
export const TRANSIENT_CAPACITY = MOB_CAPACITY * 2 + MOB_CAPACITY + Math.ceil(MOB_CAPACITY / RAMPAGE.every) * 2;
/**
 * 빌보드 일회성 용량: 위 최악에 더해, 띄운 무리를 칼날 폭풍이 연달아 칠 때 공중 연타 표식(몹마다 속도선·공중 고리 두 장, 수명 0.34초)이
 * 0.15초 간격 타격 세 번만큼 겹친다(Codex 6차: 60마리 두 번만 쳐도 240장이 필요했다). 같은 드로우콜, 인스턴스만 늘어난다
 */
export const BOARD_TRANSIENT_CAPACITY = TRANSIENT_CAPACITY + MOB_CAPACITY * 2 * 3;

export class CombatCoreFx {
  readonly group = new THREE.Group();
  /** 빌보드 가산: 지속 = 무방비 글리프(몹 최대 용량만큼), 일회성 = 몹 최대 용량 × 2(광역 한 번에 모두 처형돼도 처형마다 두 장씩 남는다) */
  readonly board = new MarkPool('board', MOB_CAPACITY, BOARD_TRANSIENT_CAPACITY, 22);
  /** 바닥 알파: 지속 = 자석 1 + 무방비 고리(몹 최대 용량만큼), 일회성 = 균열·처형(고리 두 겹)·폭주 고리, 몹 최대 용량 × 2 */
  readonly paint = new MarkPool('paint', 1 + MOB_CAPACITY, TRANSIENT_CAPACITY, 3);
  private readonly fade = new FadeTracker();
  private magnetKey = -1;
  private lx = 0; private lz = 0; private lr = 1;
  private view: GameView | null = null;
  private alphaF = 1;
  private realMs = 0;
  private scale = 1;
  private reduce = false;
  private seed = 0x2545f491;
  private vuln = 0;
  private readonly vv: VulnerableView = { alpha: 0, hz: 0, pop: 1 };
  constructor(
    private readonly particles: ParticleFx,
    private readonly flash: FlashLimiter,
    private readonly note: CoreNote,
  ) {
    this.group.name = 'combat-core-fx';
    this.group.add(this.paint.mesh, this.board.mesh);
  }

  setScale(s: number): void { this.scale = s; }
  setReduceFlash(on: boolean): void { this.reduce = on; }

  /** 매 프레임(update 전에): 게임 뷰·보간 계수·실시간(ms). 픽스처처럼 magnet·rampage가 없는 뷰도 견딘다 */
  setView(view: GameView, alpha: number, realMs: number): void {
    this.view = view; this.alphaF = alpha; this.realMs = realMs;
  }

  private rnd(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  private n(v: number): number { return Math.round(v * this.scale); }

  private burst(x: number, y: number, z: number, dir: number, spread: number, count: number, speed: number, c: readonly [number, number, number], white: number, life: number, up: number): void {
    this.particles.streaks(x, y, z, dir, spread, this.n(count), speed, c[0], c[1], c[2], white, life, up);
  }

  // ── 이벤트 ────────────────────────────────────────────

  /** 가드 브레이크(자세 붕괴) 순간의 균열 터짐. 금빛 고리·파편·불똥은 CombatFx.guardBreak가 낸다 */
  guardBreak(e: GameEvent): void {
    const boss = (e.a | 0) === 1;
    this.paint.spawn(PK.crack, e.x, GROUND_Y, e.z, boss ? 7 : 3.2, 0.6, this.rnd() * TAU, this.rnd() * 40);
    this.note('breakCrack', e.tick, e.x, e.z, e.a);
  }

  /** 공중 연타: 위로 솟는 불똥 + 작은 세로 속도선(바닥 고리·먼지 없음 — 땅 타격과 구별). e.a = 재띄우기 횟수 1..3 */
  juggle(e: GameEvent): void {
    if (this.scale <= 0) return;
    const n = Math.max(1, Math.min(3, e.a | 0));
    const y = Math.max(0.6, e.y + 0.9);
    this.board.spawn(BK.vlines, e.x, y, e.z, 1.9 + 0.25 * n, 0.34, (this.rnd() - 0.5) * 0.2);
    this.board.spawn(BK.airRing, e.x, y - 0.35, e.z, 1.7 + 0.3 * n, 0.26);
    this.burst(e.x, y - 0.2, e.z, 0, TAU, 8 + 4 * n, 3.2, GOLD, this.reduce ? 0.15 : 0.45, 0.34, 7);
    this.note('juggle', e.tick, e.x, e.z, n);
  }

  /** 공중에 뜬 몹에 들어간 타격(HitFlag.Juggle) 자체의 작은 위쪽 불똥(재띄우기 상한 뒤에도 공중 타격이 구별되게) */
  airHit(x: number, y: number, z: number): void {
    this.burst(x, Math.max(0.7, y), z, 0, TAU, 5, 2.6, GOLD, 0.3, 0.26, 5);
  }

  /**
   * 처형: 금색+진홍 교차 참격 · 바닥 충격 고리(두 겹) · 금색/진홍 불똥 · (보상받은 처형만) 짧은 흰금색 섬광.
   * 카메라 펀치·히트스톱·회복 숫자는 전투 레인 이벤트가 이미 낸다.
   */
  execute(e: GameEvent): void {
    if (this.scale <= 0) return;
    const rewarded = (e.flags & 1) !== 0;
    const y = Math.max(1, e.y + 1);
    const k = rewarded ? 1 : 0.8;
    this.board.spawn(BK.cross, e.x, y, e.z, 3.1 * k, 0.5, (this.rnd() - 0.5) * 0.3);
    this.paint.spawn(PK.exec, e.x, GROUND_Y + 0.004, e.z, 6.4 * k, 0.45);
    this.paint.spawn(PK.exec, e.x, GROUND_Y + 0.006, e.z, 4.0 * k, 0.36, 0, 0, 0.06);
    this.burst(e.x, y, e.z, 0, TAU, rewarded ? 26 : 16, 10, GOLD, 0.45, 0.4, 4);
    this.burst(e.x, y, e.z, e.d, 1.7, rewarded ? 16 : 10, 13, CRIMSON, 0.3, 0.34, 3);
    const flashed = rewarded && requestExecuteFlash(this.flash, this.realMs, this.reduce);
    this.note('execute', e.tick, e.x, e.z, flashed ? 1 : 0);
  }

  /** 폭주 단계 달성: 영웅 둘레 오라(금색 고리 + 세 줄기 상승선 + 위로 솟는 불똥). HUD 글자는 UI 레인 */
  rampage(e: GameEvent): void {
    if (this.scale <= 0) return;
    const tier = Math.max(1, Math.min(4, e.b | 0));
    const s = 1 + 0.1 * (tier - 1);
    this.paint.spawn(PK.rampage, e.x, GROUND_Y + 0.002, e.z, 7.2 * s, 0.55);
    this.paint.spawn(PK.rampage, e.x, GROUND_Y + 0.003, e.z, 4.4 * s, 0.42, 0, 0, 0.07);
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * TAU + 0.5 + this.rnd() * 0.4;
      this.board.spawn(BK.vlines, e.x + Math.sin(a) * 1.0, 1.25, e.z + Math.cos(a) * 1.0, 2.5 * s, 0.5, 0, 0, i * 0.05);
    }
    this.burst(e.x, 0.3, e.z, 0, TAU, 22, 2.4, GOLD, this.reduce ? 0.2 : 0.5, 0.55, 7);
    this.note('rampage', e.tick, e.x, e.z, tier);
  }

  // ── 매 프레임 ─────────────────────────────────────────

  /** now는 쓰지 않는다(표식 수명은 초 단위). dtSec: 영웅 시간 경과(히트스톱에 멈춤) */
  update(dtSec: number, camera: THREE.Camera): void {
    this.board.begin(); this.paint.begin();
    const v = this.view;
    if (v && combatScene(v.flow.scene)) {
      this.updateMagnet(v, dtSec, camera);
      this.updateVulnerable(v, camera);
    } else {
      this.fade.step(dtSec, false);
      this.vuln = 0;
    }
    this.board.flush(dtSec, camera);
    this.paint.flush(dtSec, camera);
  }

  private updateMagnet(v: GameView, dtSec: number, camera: THREE.Camera): void {
    const mg = v.magnet;
    let on = false;
    let key = -1;
    if (mg) {
      const m = v.mobs, a = this.alphaF;
      if (mg.boss && v.boss.state !== 0 && v.boss.state !== 10 && v.boss.state !== 11) {
        on = true; key = -2;
        this.lx = v.boss.prevX + (v.boss.x - v.boss.prevX) * a;
        this.lz = v.boss.prevZ + (v.boss.z - v.boss.prevZ) * a;
        this.lr = v.boss.radius * 1.05 + 0.4;
      } else if (mg.target >= 0 && mg.target < m.capacity) {
        const i = mg.target, st = m.state[i];
        if (st !== MobState.Inactive && st !== MobState.Dying) {
          on = true; key = i;
          this.mobPos(v, i);
          const stats = MOB_STATS[m.kind[i]];
          this.lr = stats.radius * (m.elite[i] ? MOBS.elite.scale : 1) * 1.25 + 0.38;
        }
      }
    }
    if (on && key !== this.magnetKey) { this.fade.restart(); this.magnetKey = key; }
    this.fade.step(dtSec, on);
    if (!on && this.fade.value <= 0) this.magnetKey = -1;
    if (this.fade.value > 0.003) this.paint.put(PK.magnet, this.lx, GROUND_Y, this.lz, (this.lr * 2) / RING_AT, 0.9 * this.fade.value, this.realMs / 1000, 0, 0, camera);
  }

  private px = 0; private pz = 0; private py = 0;
  /** 몹 i의 보간된 위치 → px/py/pz. 슬롯이 재사용돼 멀리 점프하면 보간하지 않는다 */
  private mobPos(v: GameView, i: number): void {
    const m = v.mobs, a = this.alphaF;
    const jump = Math.hypot(m.x[i] - m.prevX[i], m.z[i] - m.prevZ[i]) > 4;
    const k = jump ? 1 : a;
    this.px = m.prevX[i] + (m.x[i] - m.prevX[i]) * k;
    this.py = m.prevY[i] + (m.y[i] - m.prevY[i]) * k;
    this.pz = m.prevZ[i] + (m.z[i] - m.prevZ[i]) * k;
    this.lx = this.px; this.lz = this.pz;
  }

  private updateVulnerable(v: GameView, camera: THREE.Camera): void {
    const m = v.mobs, tsec = this.realMs / 1000;
    let count = 0;
    for (let i = 0; i < m.capacity; i++) {
      const left = m.guardBroken[i];
      if (left <= 0) continue;
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning) continue;
      vulnerableView(left, POSTURE.breakTicks, this.vv);
      if (this.vv.alpha <= 0.01) continue;
      this.mobPos(v, i);
      const stats = MOB_STATS[m.kind[i]];
      const es = m.elite[i] ? MOBS.elite.scale : 1;
      const ringR = stats.radius * es * 1.55 + 0.5;
      this.paint.put(PK.vuln, this.px, GROUND_Y + 0.001, this.pz, (ringR * 2) / RING_AT, this.vv.alpha, tsec, this.vv.hz, 0, camera);
      const head = this.py + HEAD_H[m.kind[i]] * es + 0.55 + Math.sin(tsec * TAU * 1.4 + i) * 0.05;
      this.board.put(BK.glyph, this.px, head, this.pz, 0.62 * this.vv.pop * (m.elite[i] ? 1.2 : 1), this.vv.alpha, tsec, this.vv.hz, 0, camera);
      count++;
    }
    this.vuln = count;
  }

  /** QA 읽기 */
  state(): CoreState {
    return {
      magnetAlpha: this.fade.value, magnetKey: this.magnetKey, vulnerable: this.vuln,
      board: this.board.drawn, paint: this.paint.drawn,
      transients: this.board.slots.n + this.paint.slots.n,
    };
  }

  prewarm(on: boolean): void { this.board.prewarm(on); this.paint.prewarm(on); }

  clear(): void {
    this.board.clear(); this.paint.clear();
    this.fade.restart(); this.magnetKey = -1; this.vuln = 0;
  }

  dispose(): void { this.board.dispose(); this.paint.dispose(); }
}
